import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import qs from 'qs';
import { useCallback, useMemo } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getSpecialPageListData } from '@src/config/getSpecialPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { FILTERS_NAMES, MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { useListPage } from '@src/hooks/useListPage';
import { Layout as AppLayout } from 'app/Layout';
import { sravniOfferAction, fullDealOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider';
import { AmountFilter } from 'features/AmountFilter';
import ProductList from 'features/ProductList/ui/ProductList/ProductList';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { FullDealEntryPoint } from 'widgets/FullDealEntryPoint';
import { useIsShowFullDealEntrypoint } from 'widgets/FullDealEntryPoint/lib';

import styles from './CrmContextPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.CRM,
  source: 'search_crm',
  sortPanelVisible: true,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
  withCardDetailsClick: true,
};

export const CrmContextPage: NextPage = () => {
  const isMobile = useIsMobile();
  const { filter, seo, pathname } = useListPage(ListKeys.CRM);

  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile }), [isMobile]);
  const { asPath } = useRouter();
  const [, query] = asPath.split('?');

  const handleUpdateFilters = useCallback(
    (key: string, value: unknown) => {
      const queryData = qs.parse(query);

      filter.updateFilterMap(
        {
          [key]: value,
          ...(queryData?.[FILTERS_NAMES.IS_SHOW_CALCULATOR] && {
            [FILTERS_NAMES.IS_SHOW_CALCULATOR]: queryData?.[FILTERS_NAMES.IS_SHOW_CALCULATOR] === 'true',
          }),
        },
        false,
      );
    },
    [filter, query],
  );
  const filterData = useMemo(() => ({ ...filter, setFilter: handleUpdateFilters }), [filter, handleUpdateFilters]);
  const isShowFullDealEntrypoint = useIsShowFullDealEntrypoint();

  return (
    <AppLayout cookiePolicyDisabled>
      <PageContext.Provider value={config}>
        <BrandingHeaders
          containerClassName={styles.page_header}
          afterHeaders={
            isShowFullDealEntrypoint ? (
              <FullDealEntryPoint heading={seo.heading} />
            ) : (
              <AmountFilter listKey={ListKeys.CRM} filters={filterData} afterContent={null} />
            )
          }
        />
        <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
          <ProductList filter={filter} pathname={pathname} />
        </CardActionProvider>
        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE, BottomBannerTypes.SIDEBAR]} />
      </PageContext.Provider>
    </AppLayout>
  );
};

CrmContextPage.getInitialProps = (ctx: Application.ReduxNextPageContext) =>
  getSpecialPageListData(ctx, {
    key: ListKeys.CRM,
    defaultFiltersMode: MODES.CRM,
  });
